import type { IconButtonProps as MuiIconButtonProps } from '@mui/material/IconButton';
import MuiIconButton from '@mui/material/IconButton';
import { forwardRef } from 'react';

/**
 * IconButton — icon-only action with optional solid fill.
 *
 * Thin wrapper over @mui/material/IconButton that adds a `variant` prop:
 *   - "text" (default) — transparent background; icon colour driven by `color` prop.
 *   - "contained" — solid brand-colour fill; icon always white.
 *
 * All styling lives in `src/theme/components.ts` (MuiIconButton). The `variant` value is
 * forwarded as `data-variant` so CSS attribute selectors in the theme can target it.
 *
 * Supported colours (per Figma):
 *   - text:      primary (purple icon), secondary (neutral grey icon = text.secondary), error (red icon)
 *   - contained: primary (purple fill), secondary (navy fill), error (red fill)
 *
 * Always provide `aria-label` — the icon alone is not accessible without it.
 *
 * SHARED sub-component: ButtonBase (see ButtonBase.tsx).
 */
export interface IconButtonProps extends Omit<MuiIconButtonProps, 'variant'> {
  /** Visual style — "text" (transparent bg) | "contained" (solid fill). Default: "text". */
  variant?: 'text' | 'contained';
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { variant = 'text', color = 'primary', size = 'large', ...props },
  ref
) {
  return <MuiIconButton ref={ref} color={color} size={size} data-variant={variant} {...props} />;
});
